import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Flame, ListTodo, RefreshCw, Activity, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import { useJiraIssueTypes, useJiraIssues, useJiraProjects, useJiraSummary } from '../hooks/useJira';
import { IssueTable } from '../components/jira/IssueTable';
import { StatCard } from '../components/jira/StatCard';
import { ProjectCard } from '../components/jira/ProjectCard';
import { StatusBreakdownChart } from '../components/jira/StatusBreakdownChart';
import { AgingIssuesPanel } from '../components/jira/AgingIssuesPanel';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { getApiErrorMessage, isApiError } from '../api/client';
import { formatRelativeTime } from '../utils/format';
import { cn } from '../utils/cn';
import { ComingSoon } from './ComingSoon';

const statusFilters = [
  { label: 'All', value: undefined },
  { label: 'To Do', value: 'To Do' },
  { label: 'In Progress', value: 'In Progress' },
  { label: 'Done', value: 'Done' },
] as const;

export function JiraBoard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectKey = searchParams.get('project') ?? undefined;
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [issueType, setIssueType] = useState<string | undefined>(undefined);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState<string | undefined>(undefined);

  // Debounce the free-text search so every keystroke doesn't fire a Jira request -
  // 400ms feels instant without hammering the API while someone's still typing.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(searchInput.trim() || undefined), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const summary = useJiraSummary();
  const { data: projects } = useJiraProjects();
  const { data: issueTypes } = useJiraIssueTypes();
  const { data, isLoading, isError, error } = useJiraIssues({
    projectKey,
    status,
    issueType,
    query,
    maxResults: 50,
  });

  if (summary.isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (isApiError(summary.error) && summary.error.response.status === 503) {
    return <ComingSoon icon={Activity} title="Jira" integrationLabel="Jira" />;
  }

  if (summary.isError || !summary.data) {
    return (
      <Card className="p-8 text-center">
        <AlertTriangle className="mx-auto mb-3 text-rose-500" size={28} />
        <p className="font-medium text-slate-700 dark:text-slate-200">Couldn&apos;t load Jira data</p>
        <p className="mt-1 text-sm text-slate-400">{getApiErrorMessage(summary.error)}</p>
      </Card>
    );
  }

  const s = summary.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-400">
          Live from Jira &middot; updated {formatRelativeTime(s.fetchedAt)}
        </p>
        <button
          onClick={() => summary.refetch()}
          className="flex items-center gap-1.5 text-sm font-medium text-pulse-600 hover:text-pulse-700 dark:text-pulse-400"
        >
          <RefreshCw size={14} className={summary.isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Open issues" value={s.totalOpenIssues} icon={ListTodo} index={0} />
        <StatCard label="High priority open" value={s.highPriorityOpen} icon={Flame} tone="danger" index={1} />
        <StatCard label="Overdue" value={s.overdueIssues} icon={AlertTriangle} tone="warning" index={2} />
        <StatCard label="Closed this week" value={s.totalDoneThisWeek} icon={CheckCircle2} tone="success" index={3} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Why is this taking long?</CardTitle>
          </CardHeader>
          <CardBody>
            <AgingIssuesPanel
              staleIssues={s.staleIssues}
              oldestOpenIssues={s.oldestOpenIssues}
              avgOpenIssueAgeDays={s.avgOpenIssueAgeDays}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Status breakdown</CardTitle>
          </CardHeader>
          <CardBody>
            <StatusBreakdownChart data={s.statusBreakdown} />
          </CardBody>
        </Card>
      </div>

      {s.projects.length > 0 && (
        <div>
          <h2 className="mb-3 text-sm font-semibold text-slate-500 dark:text-slate-400">Projects</h2>
          <motion.div layout className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {s.projects.map((project, i) => (
              <ProjectCard key={project.id} project={project} index={i} />
            ))}
          </motion.div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search summary, description, comments..."
            className="h-9 w-64 rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-700 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          />
        </div>

        <select
          value={projectKey ?? ''}
          onChange={(e) => {
            const value = e.target.value;
            setSearchParams(value ? { project: value } : {});
          }}
          className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          <option value="">All projects</option>
          {projects?.map((p) => (
            <option key={p.id} value={p.key}>
              {p.name} ({p.key})
            </option>
          ))}
        </select>

        <select
          value={issueType ?? ''}
          onChange={(e) => setIssueType(e.target.value || undefined)}
          className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          <option value="">All types</option>
          {issueTypes?.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-800">
          {statusFilters.map((f) => (
            <button
              key={f.label}
              onClick={() => setStatus(f.value)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                status === f.value
                  ? 'bg-pulse-600 text-white'
                  : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {data && <span className="ml-auto text-sm text-slate-400">{data.total} issues</span>}
      </div>

      {isError ? (
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-3 text-rose-500" size={28} />
          <p className="font-medium text-slate-700 dark:text-slate-200">Couldn&apos;t load issues</p>
          <p className="mt-1 text-sm text-slate-400">{getApiErrorMessage(error)}</p>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Browse issues</CardTitle>
          </CardHeader>
          <CardBody>
            <IssueTable issues={data?.issues ?? []} loading={isLoading} />
          </CardBody>
        </Card>
      )}
    </div>
  );
}